import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibTextarea } from '@chewy/kib-fields-new-react'

import { FeedbackType } from '~/components/common/RhapFeedbackButton'
import i18n from '~/locales/i18n'

import {
  NEGATIVE_FEEDBACK_REASON,
  type NegativeFeedbackReasonKey,
} from './agentChatFeedbackReasons'
import { AgentResponseButton } from './AgentResponseButton'
import { MessageBubble } from './MessageBubble'

import styles from './ChatMessage.module.scss'

const MAX_FEEDBACK_CHARS = 200

const REASON_KEYS: Array<{
  key: NegativeFeedbackReasonKey
  translationString: string
}> = [
  {
    key: NEGATIVE_FEEDBACK_REASON.NOT_WHAT_I_ASKED,
    translationString: i18n.t('Agent:FEEDBACK_REASON_NOT_WHAT_I_ASKED'),
  },
  {
    key: NEGATIVE_FEEDBACK_REASON.INCORRECT_OR_INCOMPLETE,
    translationString: i18n.t('Agent:FEEDBACK_REASON_INCORRECT_OR_INCOMPLETE'),
  },
  {
    key: NEGATIVE_FEEDBACK_REASON.HARD_TO_UNDERSTAND,
    translationString: i18n.t('Agent:FEEDBACK_REASON_HARD_TO_UNDERSTAND'),
  },
  {
    key: NEGATIVE_FEEDBACK_REASON.NOT_HELPFUL_ENOUGH,
    translationString: i18n.t('Agent:FEEDBACK_REASON_NOT_HELPFUL_ENOUGH'),
  },
]

export interface AgentFeedbackMessageProps {
  feedback: FeedbackType | null
  negativeReason: NegativeFeedbackReasonKey | null
  onReasonClick: (reasonKey: NegativeFeedbackReasonKey) => void
  onSendFeedback: (feedbackText: string) => void
}

export const AgentFeedbackMessage: React.FC<AgentFeedbackMessageProps> = ({
  feedback,
  negativeReason,
  onReasonClick,
  onSendFeedback,
}) => {
  const { t } = useTranslation('Agent')
  const [customFeedbackText, setCustomFeedbackText] = useState('')
  const [showMoreTextarea, setShowMoreTextarea] = useState(false)
  const charsRemaining = Math.max(
    0,
    MAX_FEEDBACK_CHARS - customFeedbackText.length,
  )
  const charsOver = Math.max(0, customFeedbackText.length - MAX_FEEDBACK_CHARS)
  const isOverLimit = charsOver > 0

  const handleMoreClick = () => {
    setShowMoreTextarea(true)
  }

  const handleSendFeedback = () => {
    const trimmed = customFeedbackText.slice(0, MAX_FEEDBACK_CHARS).trim()
    onSendFeedback(trimmed)
    setShowMoreTextarea(false)
  }

  const showAppreciationBubble =
    feedback === FeedbackType.NEGATIVE && negativeReason !== null
  const showNegativeReasonUI =
    feedback === FeedbackType.NEGATIVE && negativeReason === null

  if (feedback === null) {
    return null
  }

  if (feedback === FeedbackType.POSITIVE) {
    return (
      <div className={styles.feedbackFollowUpBubbles}>
        <MessageBubble sender="agent">
          <p className={styles.messageContent}>
            {t('FEEDBACK_THANKS_POSITIVE')}
          </p>
        </MessageBubble>
      </div>
    )
  }

  return (
    <div className={styles.feedbackFollowUpBubbles}>
      <MessageBubble sender="agent">
        <p className={styles.messageContent}>
          {t('FEEDBACK_THANKS_NEGATIVE_PROMPT')}
        </p>
        {showNegativeReasonUI && (
          <>
            <div className={styles.feedbackReasonButtons}>
              {REASON_KEYS.map(({ key, translationString }) => (
                <AgentResponseButton
                  emphasis="tertiary"
                  key={key}
                  onClick={() => onReasonClick(key)}
                >
                  {translationString}
                </AgentResponseButton>
              ))}
              <AgentResponseButton
                emphasis={showMoreTextarea ? 'primary' : 'tertiary'}
                theme={showMoreTextarea ? 'action' : 'utility'}
                onClick={handleMoreClick}
              >
                {t('FEEDBACK_REASON_MORE')}
              </AgentResponseButton>
            </div>
            {showMoreTextarea && (
              <div className={styles.feedbackMoreSection}>
                <KibTextarea
                  help={
                    isOverLimit
                      ? undefined
                      : t('FEEDBACK_CHARACTERS_REMAINING', {
                          charsRemaining,
                        })
                  }
                  helpPlacement="bottom"
                  invalid={isOverLimit}
                  label={t('FEEDBACK_MORE_LABEL')}
                  messagesContent={
                    isOverLimit
                      ? t('FEEDBACK_CHARACTERS_OVER_LIMIT', {
                          charsOver,
                        })
                      : undefined
                  }
                  placeholder={t('FEEDBACK_MORE_PLACEHOLDER')}
                  rows={3}
                  value={customFeedbackText}
                  onChange={(e) => setCustomFeedbackText(e.target.value)}
                />
                <AgentResponseButton
                  disabled={isOverLimit}
                  emphasis="primary"
                  theme="action"
                  onClick={handleSendFeedback}
                >
                  {t('FEEDBACK_SEND_BUTTON')}
                </AgentResponseButton>
              </div>
            )}
          </>
        )}
        {negativeReason !== null && (
          <div className={styles.feedbackReasonButtons}>
            {REASON_KEYS.map(({ key, translationString }) => (
              <AgentResponseButton
                disabled
                emphasis={negativeReason === key ? 'primary' : 'tertiary'}
                key={key}
                theme={negativeReason === key ? 'action' : 'utility'}
              >
                {translationString}
              </AgentResponseButton>
            ))}
            <AgentResponseButton
              disabled
              emphasis={
                negativeReason === NEGATIVE_FEEDBACK_REASON.MORE
                  ? 'primary'
                  : 'tertiary'
              }
              theme={
                negativeReason === NEGATIVE_FEEDBACK_REASON.MORE
                  ? 'action'
                  : 'utility'
              }
            >
              {t('FEEDBACK_REASON_MORE')}
            </AgentResponseButton>
          </div>
        )}
      </MessageBubble>

      {showAppreciationBubble && (
        <MessageBubble sender="agent">
          <p className={styles.messageContent}>{t('FEEDBACK_APPRECIATION')}</p>
        </MessageBubble>
      )}
    </div>
  )
}

export default AgentFeedbackMessage
